<script lang="ts">
  import { onMount } from "svelte"
  import Sortable from "sortablejs"
  import { flip } from "svelte/animate"
  import LongTermGoal from "./LongTermGoal.svelte"
  import { xhr, toast, sortElementsForSortable, wait } from "$app/utils"
  import { appStore } from "$app/store.svelte"
  import Honeybadger from "$app/utils/honeybadger"

  let { expandGoals = $bindable(false) }: { expandGoals: boolean } = $props()

  let sortableSection = $state<HTMLElement | null>(null)
  const flipDurationMs = 300
  let goals: GoalType[] = $state([])

  function setSortable(): void {
    const delay = 20
    let elem: HTMLElement

    if (!sortableSection) return
    const sort = Sortable.create(sortableSection, {
      animation: 200,
      group: "LongTermGoal",
      scrollSensitivity: 150,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      emptyInsertThreshold: 20,
      removeCloneOnHide: false,
      onEnd: async (e: EventSortType) => {
        sort.options.sort = false

        // No move
        if (e.newIndex === e.oldIndex) {
          return
        }

        // Same container
        if (e.newIndex !== e.oldIndex) {
          const currentGoals = appStore.goals
            .filter((g) => g.type === "LongTermGoal")
            .sort((a, b) => a.parent_position - b.parent_position)

          let newGoals = sortElementsForSortable(
            currentGoals,
            e.newIndex,
            e.oldIndex
          ) as GoalType[]

          newGoals = await saveSortOrder(newGoals, appStore.root_goal.id)

          newGoals.forEach((goal) => {
            const idx = appStore.goals.findIndex((g) => goal.id === g.id)
            appStore.goals[idx] = { ...appStore.goals[idx], ...goal }
          })

          goals = appStore.goals
            .filter((g) => g.type === "LongTermGoal")
            .sort((a, b) => a.parent_position - b.parent_position)

          sort.options.sort = true
          return
        }

        sort.options.sort = true
      },
      onStart: (evt: EventSortType) => {
        appStore.draggedElement = {
          id: parseInt(evt.item.dataset.id),
          type: "LongTermGoal"
        } as draggedElemType
      }
    })

    elem = sort.el
  }

  async function saveSortOrder(
    newGoals: GoalType[],
    parent_goal_id: number
  ): Promise<any> {
    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/goals/sort_by_parent.json`,
        { goals: newGoals.map((g) => g.id), id: parent_goal_id }
      )
      return resp.data.goals
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating the sort order", "error-")

      const tmpGoals = [...appStore.goals]
      appStore.goals = []
      await wait(10)
      appStore.goals = tmpGoals
      return false
    }
  }

  onMount(() => {
    goals = appStore.goals
      .filter((g) => g.type === "LongTermGoal")
      .sort((a, b) => a.parent_position - b.parent_position)

    if (appStore.editable) {
      setSortable()
    }
  })
</script>

<section class="mt-6" bind:this={sortableSection}>
  {#key goals}
    {#each goals as goal, idx (goal.id)}
      <div
        animate:flip={{ duration: flipDurationMs }}
        class="mb-2"
        data-id={goal.id}
      >
        <LongTermGoal
          goal={goals[idx]}
          pos={idx + 1}
          bind:expanded={expandGoals}
        />
      </div>
    {/each}
  {/key}
</section>
